import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Landmark, Building2, Wrench, Check } from "lucide-react";
import { StablePanels, selectPanelByKey } from './StablePanels';

const AUDIENCES = [
  {
    key: "program",
    icon: Landmark,
    tabLabel: "Government & Program Offices",
    eyebrow: "Government & Program Offices",
    title: "Support mission-critical programs with a U.S. manufacturing partner built for compliance, traceability, and dependable delivery.",
    body: "Every shipment includes the documentation, traceability, and qualification evidence needed to support audits, compliance, and program milestones.",
    bullets: [
      "ITAR-Registered, U.S.-Based Operations",
      "ISO 9001 & ISO 14001 Certified Management System",
      "Qualification & Acceptance Documentation",
      "Lot-Level Traceability & Serialization",
      "Reliable Delivery to Program Milestones",
    ],
    cta: { label: "Explore Capabilities", to: "/capabilities" },
  },
  {
    key: "primes",
    icon: Building2,
    tabLabel: "Prime Contractors",
    eyebrow: "Prime Contractors",
    title: "A manufacturing partner built for your supply chain.",
    body: "Energetics, mechanical assemblies, and qualification under one roof from a single ITAR-registered U.S. facility, delivering integrated manufacturing, traceability, and production at scale.",
    bullets: [
      "Scalable Production Capacity, Including Surge Demand",
      "Single-Source Manufacturing for Energetics & Mechanical Assemblies",
      "Proven On-Time Delivery for Active Defense Programs",
      "U.S.-Based, ITAR-Registered Manufacturing",
      "Dedicated Program & Engineering Support",
    ],
    cta: { label: "Discuss a Partnership", to: "/contact-us" },
  },
  {
    key: "engineers",
    icon: Wrench,
    tabLabel: "Engineering Teams",
    eyebrow: "Engineering Teams",
    title: "Engineering support beyond production.",
    body: "Detailed specifications, qualification and validation data, and direct engineer-to-engineer support help streamline integration, reduce technical risk, and accelerate acceptance.",
    bullets: [
      "Detailed Product Specifications & Interface Data",
      "MIL-STD Qualification & Acceptance Reports",
      "Validation Data Included with Deliverables",
      "Custom Configuration & Qualification Support",
      "Direct Engineer-to-Engineer Collaboration",
    ],
    cta: { label: "Review Quality & Testing", to: "/quality-testing" },
  },
];

export default function AudienceSwitcher({ sequential = false, descriptions = {} }) {
  const [active, setActive] = useState(0);

  return (
    <div data-testid="audience-switcher">
      {/* Tabs */}
      <div role="tablist" aria-label="Program teams" className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ivory/10 border-t border-b border-ivory/15">
        {AUDIENCES.map((aud, i) => {
          const Icon = aud.icon;
          const isActive = i === active;
          return (
            <button
              key={aud.key}
              type="button"
              role="tab"
              id={`audience-option-${aud.key}`}
              aria-selected={isActive}
              aria-controls={`audience-content-${aud.key}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActive(i)}
              onKeyDown={event => selectPanelByKey(event, i, AUDIENCES.length, setActive)}
              data-testid={`audience-tab-${aud.key}`}
              className={`relative group text-left px-4 md:px-5 lg:px-8 py-4 md:py-5 lg:py-7 transition-colors duration-500 min-w-0 ${
                isActive ? "bg-navy" : "bg-navy/95 hover:bg-navy-700"
              }`}
            >
              {/* Active underline — uses a scale-from-left transform driven
                  by `isActive` instead of framer-motion's `layoutId`. This
                  keeps the smooth in/out reveal while allowing us to load
                  only the domAnimation feature bundle. */}
              <span
                aria-hidden="true"
                className={`absolute top-0 left-0 right-0 h-[2px] bg-signal origin-left transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                  isActive ? "scale-x-100" : "scale-x-0"
                }`}
              />
              <div className="flex items-center gap-3 md:gap-3 lg:gap-4 min-w-0">
                <div
                  className={`w-9 h-9 md:w-9 md:h-9 lg:w-10 lg:h-10 flex items-center justify-center border shrink-0 transition-colors ${
                    isActive
                      ? "bg-signal border-signal text-white"
                      : "border-ivory/25 text-ivory/70 group-hover:border-ivory/60"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.7} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-base md:text-[15px] lg:text-2xl tracking-tight leading-tight text-ivory break-words">
                    {aud.tabLabel}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Panel */}
      <div className="bg-navy border border-t-0 border-ivory/15 px-5 md:px-12 py-8 md:py-16 relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="absolute top-0 left-0 w-24 h-px bg-signal/60" />

        <StablePanels sequential={sequential} items={AUDIENCES} activeIndex={active} data-testid="audience-panels" renderPanel={a => (
          <div
            role="tabpanel" id={`audience-content-${a.key}`} aria-labelledby={`audience-option-${a.key}`}
            className="relative max-w-4xl min-w-0"
            data-testid={`audience-panel-${a.key}`}
          >
            {/* Eyebrow */}
            <div className="font-mono text-[10.5px] md:text-[14px] tracking-[0.14em] md:tracking-[0.32em] text-signal uppercase mb-4 md:mb-5 flex flex-wrap items-center gap-x-3 gap-y-1 break-words">
              <span className="w-6 h-px bg-signal shrink-0" />
              <span>{a.eyebrow}</span>
            </div>

            {/* Large headline (differentiator-led) */}
            <h3 className="font-display font-bold text-2xl md:text-4xl lg:text-[44px] text-ivory leading-[1.15] tracking-tight text-balance max-w-3xl break-words">
              {a.title}
            </h3>

            {/* Short paragraph */}
            <p className="mt-4 md:mt-6 text-ivory/80 font-body font-light text-base md:text-xl leading-[1.6] max-w-2xl">
              {descriptions[a.key] || a.body}
            </p>

            {/* Scannable proof points */}
            <ul className="mt-6 md:mt-9 grid sm:grid-cols-2 gap-x-10 gap-y-3 md:gap-y-3.5 max-w-3xl">
              {a.bullets.map((b, i) => (
                <li
                  key={b}
                  data-testid={`audience-point-${a.key}-${i}`}
                  className="flex items-start gap-3 text-[14.5px] md:text-[15.5px] text-ivory/90 font-body break-words"
                >
                  <span className="mt-1 w-5 h-5 shrink-0 flex items-center justify-center border border-signal/60 text-signal">
                    <Check className="w-3 h-3" strokeWidth={2.5} />
                  </span>
                  <span className="leading-snug pt-0.5 min-w-0">{b}</span>
                </li>
              ))}
            </ul>

            {/* Action */}
            <Link
              to={a.cta.to}
              data-testid={`audience-cta-${a.key}`}
              className="mt-8 md:mt-10 inline-flex items-center gap-2 group font-industrial font-semibold uppercase tracking-[0.14em] md:tracking-[0.22em] text-[12px] md:text-[14px] text-ivory hover:text-signal transition-colors"
            >
              <span className="link-edge">{a.cta.label}</span>
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        )} />
      </div>
    </div>
  );
}
